import { DesktopTimePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';

/** How the user committed the time: via the popper UI or by typing in the input (Enter/blur). */
export type TimeChangeSource = 'picker' | 'input';

export interface TimeChangeMeta {
  source: TimeChangeSource;
}

export interface StreamTimePickerButtonProps {
  /** Current timestamp (ms). */
  value?: number | null;
  /** Called when the user commits a time. Second arg indicates picker vs typed input. */
  onChange: (timestamp: number, meta: TimeChangeMeta) => void;
  /** When true, the picker trigger is disabled. */
  disabled?: boolean;
  /** IANA timezone (e.g. America/New_York). When set, the picker shows and edits time in this zone. */
  timezone?: string;
  /** End adornment to show in the picker. */
  endAdornment?: React.ReactNode;
  /** Width of the picker. */
  width?: string;
}

/** Height to match MUI small button (36px). */
const SMALL_BUTTON_HEIGHT = 36.1;
const LARGE_BUTTON_HEIGHT = 42;
const BUTTON_HEIGHT = LARGE_BUTTON_HEIGHT;

const COLOR = '#212729';

/**
 * Time picker that uses MUI DesktopTimePicker with a trigger styled like a
 * standalone outlined button. Notifies the parent only on commit (Enter, blur,
 * or picker close/accept), not on every keystroke, to avoid unnecessary stream fetches.
 * When timezone is passed, the picker displays and edits time in that zone.
 */
export function TimePicker({
  value,
  onChange,
  disabled = false,
  timezone,
  endAdornment,
  width,
}: StreamTimePickerButtonProps) {
  const toDateTime = (ts: number | null) => (ts != null ? DateTime.fromMillis(ts) : null);

  const [localValue, setLocalValue] = useState<DateTime | null>(() => toDateTime(value ?? null));
  const latestValueRef = useRef<DateTime | null>(localValue);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync from parent when parent value changes (e.g. from another control).
  useEffect(() => {
    const next = toDateTime(value ?? null);
    setLocalValue(next);
    latestValueRef.current = next;
  }, [value]);

  latestValueRef.current = localValue;

  /** Commit current value to parent only when valid; then blur and clear selection. */
  const commitToParent = (dt: DateTime | null, source: TimeChangeSource) => {
    if (dt?.isValid) {
      onChange(dt.toMillis(), { source });
      inputRef.current?.blur();
      window.getSelection()?.removeAllRanges();
    }
  };

  /** Called when user accepts in popper (OK, close, click outside). */
  const handleAccept = (newValue: DateTime | null) => {
    setLocalValue(newValue);
    if (newValue?.isValid) {
      commitToParent(newValue, 'picker');
    }
  };

  /** Commit on text field blur (user leaves the input). */
  const handleFieldBlur = () => {
    commitToParent(latestValueRef.current, 'input');
  };

  /** Commit on Enter in the text field. */
  const handleFieldKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      commitToParent(latestValueRef.current, 'input');
    }
  };

  return (
    <DesktopTimePicker
      value={localValue}
      onChange={(newValue: DateTime | null) => setLocalValue(newValue?.isValid ? newValue : null)}
      onAccept={handleAccept}
      disabled={disabled}
      ampm={false}
      timezone={timezone}
      inputRef={inputRef}
      slotProps={{
        popper: {
          sx: {
            '& *': {
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            },
          },
        },
        textField: {
          size: 'small',
          InputProps: {
            // startAdornment is the right side of the input
            // because we reversed the flex direction
            startAdornment: endAdornment,
          },
          onBlur: handleFieldBlur,
          onKeyDown: handleFieldKeyDown,
          sx: {
            width: 'fit-content',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: disabled ? 'rgba(0, 0, 0, 0.12) !important' : `${COLOR} !important`,
              borderWidth: '1px !important',
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: `${COLOR} !important`,
            },
            '& .MuiInputBase-root': {
              background: 'transparent !important',
              flexDirection: 'row-reverse', // Alternative hack if adornmentPosition is stubborn
              height: BUTTON_HEIGHT,
              minHeight: BUTTON_HEIGHT,
              boxSizing: 'border-box',
              width: 'fit-content',
              minWidth: 0,
              cursor: 'pointer',
            },
            '.MuiInputBase-input': {
              background: 'transparent !important',
              textAlign: 'center',
              paddingY: 0,
              height: '100%',
              width: width ?? '5ch', // HH:MM = 5 chars so field shrinks to content
              minWidth: width ?? '5ch',
              fontSize: (theme) => theme.typography.pxToRem(15),
              fontWeight: (theme) => theme.typography.fontWeightMedium,
              color: disabled ? 'rgba(0, 0, 0, 0.26) !important' : COLOR,
              WebkitTextFillColor: disabled ? 'rgba(0, 0, 0, 0.26) !important' : COLOR,
            },
          },
        },
        openPickerIcon: {
          sx: {
            fontSize: '1.25rem',
            color: disabled ? 'action.disabled' : 'secondary.dark',
          },
        },
      }}
    />
  );
}
